/**
 * SignalR client for `/hubs/notifications` — mirrors
 * `UI/web_app/lib/core/services/notification_signalr_service.dart`.
 */
import {
  HttpTransportType,
  HubConnectionBuilder,
  type HubConnection,
} from '@microsoft/signalr'

import { getPublicEnv } from '@/config/public-env'

import { parseInAppNotification, type InAppNotification } from './notifications-types'

export type NotificationSignalRTokenProvider = () => string | null | Promise<string | null>

export type NotificationSignalRClientOptions = {
  /** Bearer token for hub auth and reconnect. */
  getAccessToken: NotificationSignalRTokenProvider
  /** Full hub URL override (otherwise derived from env). */
  hubUrl?: string
  /** REST base used when hub URL is derived. */
  apiBaseUrl?: string
  /** `VITE_SIGNALR_HUB_URL` override when hub URL is derived. */
  signalrHubUrl?: string | undefined
}

export function buildNotificationHubUrl(
  apiBaseUrl: string,
  signalrHubUrl?: string | undefined,
): string {
  const override = typeof signalrHubUrl === 'string' ? signalrHubUrl.trim() : ''
  if (override.length > 0) return override.replace(/\/$/, '')
  return `${apiBaseUrl.replace(/\/$/, '')}/hubs/notifications`
}

type NotificationSubscriber = (notification: InAppNotification) => void

export class NotificationSignalRClient {
  private hub: HubConnection | null = null
  private connectedToken: string | null = null
  private readonly subscribers = new Set<NotificationSubscriber>()
  private readonly getAccessToken: NotificationSignalRTokenProvider
  private readonly hubUrl: string

  constructor(options: NotificationSignalRClientOptions) {
    const env = getPublicEnv()
    this.getAccessToken = options.getAccessToken
    this.hubUrl =
      options.hubUrl ??
      buildNotificationHubUrl(
        options.apiBaseUrl ?? env.apiBaseUrl,
        options.signalrHubUrl ?? env.signalrHubUrl,
      )
  }

  /** Subscribe to parsed in-app notifications from the hub. */
  subscribe(listener: NotificationSubscriber): () => void {
    this.subscribers.add(listener)
    return () => {
      this.subscribers.delete(listener)
    }
  }

  private emit(notification: InAppNotification): void {
    for (const listener of this.subscribers) {
      listener(notification)
    }
  }

  /**
   * Connect when a token is available. Skips reconnect when the token is unchanged
   * and a hub connection already exists (matches Flutter `connectIfNeeded`).
   */
  async connectIfNeeded(token?: string | null): Promise<void> {
    const resolved =
      token !== undefined ? token : await Promise.resolve(this.getAccessToken())

    if (resolved == null || resolved.trim().length === 0) {
      await this.disconnect()
      return
    }

    const trimmed = resolved.trim()
    if (this.hub != null && this.connectedToken === trimmed) {
      return
    }

    await this.disconnect()

    try {
      const hub = new HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: async () => {
            const current = await Promise.resolve(this.getAccessToken())
            return current?.trim() ?? ''
          },
          transport: HttpTransportType.WebSockets,
          skipNegotiation: false,
        })
        .withAutomaticReconnect()
        .build()

      hub.on('ReceiveNotification', (...args: unknown[]) => {
        if (args.length === 0) return
        const parsed = parseInAppNotification(args[0])
        if (parsed) this.emit(parsed)
      })

      await hub.start()
      this.hub = hub
      this.connectedToken = trimmed
    } catch (e: unknown) {
      console.error('NotificationSignalRClient connect failed:', e)
    }
  }

  async disconnect(): Promise<void> {
    const h = this.hub
    this.hub = null
    this.connectedToken = null
    if (h != null) {
      try {
        await h.stop()
      } catch {
        // ignore stop errors during teardown
      }
    }
  }
}

export function createNotificationSignalRClient(
  options: NotificationSignalRClientOptions,
): NotificationSignalRClient {
  return new NotificationSignalRClient(options)
}
